// textKind — what a text block is in the block menu's "Turn into" (pen Q76):
// a Heading, a Subheading or Text. Two heading levels under the page title:
// Heading is level 2 and Subheading level 3. A heading stored at another
// level shows as the nearer of the two (level 1 as Heading, 4-6 as
// Subheading) and keeps its level until the author turns it into something
// else: no data changes by showing it. Pure.

import { exhaustSwitchCase } from "@bilda/utils";
import { headingToProse, proseToHeading } from "./edit.js";
import type { Element, HeadingElement } from "./types.js";

export type TextKind = "heading" | "subheading" | "text";

// The level each heading kind is stored at.
const levelOf = (kind: Exclude<TextKind, "text">): HeadingElement["level"] => {
    switch (kind) {
        case "heading":
            return 2;
        case "subheading":
            return 3;
        default:
            return exhaustSwitchCase(kind);
    }
};

// What the block is (undefined: a kind that turns into nothing, as a
// question or an objective).
export const textKindOf = (element: Element): TextKind | undefined => {
    switch (element.type) {
        case "heading":
            return element.level <= 2 ? "heading" : "subheading";
        case "prose":
            return "text";
        case "objective":
        case "note":
        case "mcq":
        case "part":
            return undefined;
        default:
            return exhaustSwitchCase(element);
    }
};

// What turning `element` into `to` takes: nothing (it already is), a new
// level for a heading, or the block retyped in place (same id).
export type TurnInto =
    | { readonly type: "none" }
    | { readonly type: "level"; readonly level: HeadingElement["level"] }
    | { readonly type: "retype"; readonly to: Element };

export const turnIntoOf = (element: Element, to: TextKind): TurnInto => {
    if (textKindOf(element) === to) return { type: "none" };
    switch (element.type) {
        case "heading":
            return to === "text"
                ? { type: "retype", to: headingToProse(element) }
                : { type: "level", level: levelOf(to) };
        case "prose":
            return to === "text"
                ? { type: "none" }
                : { type: "retype", to: proseToHeading(element, levelOf(to)) };
        case "objective":
        case "note":
        case "mcq":
        case "part":
            return { type: "none" };
        default:
            return exhaustSwitchCase(element);
    }
};
